import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { TPlayerSearch, TShortlistAll } from "@/types/trpc.types";
import { useTRPC } from "@/trpc";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { v4 as uuidv4 } from 'uuid';
import { toast } from "sonner";
import { TRPCClientError } from "@trpc/client";
import * as Icons from "lucide-react";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import SelectPosition from "@/components/features/players/select-position";
import SelectRolePosition from "@/components/features/players/select-role-position";
import { Input } from "@/components/ui/input";
import { set } from "mongoose";

type ListType = "shortlist" | "longlist";

interface SelectListDialogProps {
  trigger?: React.ReactNode;
  Player: Player;
  onSuccess?: () => void;
  isInShortlist?: boolean;
  isInLonglist?: boolean;
}

type Player = TPlayerSearch['players'][number];

const listSchema = z.object({
  position: z.string().optional(),
  profile: z.string().optional(),
  information_td: z.string().optional(),
  decision: z.string().optional(),
  advice: z.string().optional(),
  screening: z.boolean(),
  scouting: z.boolean(),
  data: z.boolean(),
  check: z.boolean(),
  active: z.boolean(),
  dataPdf: z.string().optional(),
  file1: z.string().optional(),
  file2: z.string().optional(),
  file3: z.string().optional(),
});

export type listSchema = z.infer<typeof listSchema>;

export function SelectListDialog({ trigger, Player, onSuccess, isInShortlist, isInLonglist }: SelectListDialogProps) {
  const queryClient = useQueryClient();
  const { trpc } = useTRPC();
  const [open, setOpen] = useState(false);

  const getInitialListType = (): ListType => {
    if (isInLonglist && !isInShortlist) return "shortlist";
    return "longlist";
  };

  const [listType, setListType] = useState<null | ListType>(getInitialListType());

  const form = useForm<listSchema>({
    resolver: zodResolver(listSchema),
    defaultValues: {
      position: "",
      profile: "",
      information_td: "",
      decision: "",
      advice: "",
      screening: false,
      scouting: false,
      data: false,
      check: false,
      active: true,
      dataPdf: "",
      file1: "",
      file2: "",
      file3: "",
    }
  });

  const formValues = form.watch();
  const isFormValid = listSchema.safeParse(formValues).success;

  const { mutate: addToShortlist, isLoading: isAddingToShortlist } = useMutation({
    mutationFn: async (data: { mongo_player_id: string } & listSchema) => {
      await trpc.shortlist.create.mutate(data);
    },
    onSuccess: () => {
      toast.success("Speler toegevoegd aan de shortlist");
      queryClient.invalidateQueries({ queryKey: ['shortlist'] });
      onSuccess?.();
      setOpen(false);
      resetForm();
    },
    onError: (err) => {
      if(err instanceof Error) {
        toast.error(err.message);
      } else if(err instanceof TRPCClientError) {
        toast.error(err.message);
      } else {
        toast.error("Er is een onbekende fout opgetreden.");
      }
    }
  });

  const { mutate: addToLonglist, isLoading: isAddingToLonglist } = useMutation({
    mutationFn: async (data: { mongo_player_id: string } & listSchema) => {
      await trpc.longlist.create.mutate(data);
    },
    onSuccess: () => {
      toast.success("Speler toegevoegd aan de longlist");
      queryClient.invalidateQueries({ queryKey: ['longlist'] });
      onSuccess?.();
      setOpen(false);
      resetForm();
    },
    onError: (err) => {
      if(err instanceof Error) {
        toast.error(err.message);
      } else if(err instanceof TRPCClientError) {
        toast.error(err.message);
      } else {
        toast.error("Er is een onbekende fout opgetreden.");
      }
    }
  });

  const resetForm = () => {
    setListType(getInitialListType());
    form.reset();
  };

  const handleShortlistClick = () => {
    setListType("shortlist");
  };

  const handleLonglistClick = () => {
    setListType("longlist");
  };

  const handleConfirm = () => {
    if (!Player.id) {
      toast.error("Speler ID ontbreekt");
      return;
    }

    const values = form.getValues();
    const isValid = listSchema.safeParse(values).success;
    
    if (!isValid) {
      toast.error("Formulier is niet correct ingevuld");
      return;
    }
    
    if (listType === "shortlist") {
      addToShortlist({
        mongo_player_id: Player.id.toString(),
      ...values,
    });
    } else if (listType === "longlist") {
    addToLonglist({
      mongo_player_id: Player.id.toString(),
      ...values,
    });
 }};
  const isLoading = isAddingToShortlist || isAddingToLonglist;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) resetForm();
    }}>
      <DialogTrigger asChild>
        {trigger || <Button>Toevoegen aan lijst</Button>}
      </DialogTrigger>
      <DialogContent className="p-0 rounded-3xl gap-0 overflow-hidden" hideCloseButton>
          <div className="grid grid-cols-2 gap-1 p-2">
            <Button 
              className={`bg-transparent text-white font-medium hover:bg-muted ${listType === "shortlist" ? "font-semibold border-b-2 border-b-white" : ""}`} 
              onClick={handleShortlistClick} 
              disabled={isLoading || !!isInShortlist}
            >
              ShortList
            </Button>
            <Button 
              className={`bg-transparent text-white font-medium hover:bg-muted ${listType === "longlist" ? "font-semibold border-b-2 border-b-white" : ""}`} 
              onClick={handleLonglistClick} 
              disabled={isLoading || !!isInLonglist}
            >
              LongList
            </Button>
          </div>
          <>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="space-y-2">
                      <Label htmlFor="position">Positie</Label>
                      <Controller
                        control={form.control}
                        name="position"
                        render={({ field }) => (
                          <SelectPosition
                            selectedValue={field.value}
                            onValueChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="profile">Profiel</Label>
                      <Controller
                        control={form.control}
                        name="profile"
                        render={({ field }) => (
                          <SelectRolePosition
                            selectedValue={field.value}
                            onValueChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="informationTD">Informatie TD</Label>
                  <Textarea
                    id="informationTD"
                    placeholder="Voeg informatie toe over deze speler..."
                    {...form.register("information_td")}
                    className="resize-none"
                    rows={3}
                    disabled={isLoading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="decision">Besluit</Label>
                  <Textarea
                    id="decision"
                    placeholder="Voeg een besluit toe over deze speler..."
                    {...form.register("decision")}
                    className="resize-none"
                    rows={3}
                    disabled={isLoading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="advice">Advies</Label>
                  <Controller
                    control={form.control}
                    name="advice"
                    render={({ field }) => (
                      <Select 
                        value={field.value} 
                        onValueChange={field.onChange} 
                        disabled={isLoading}
                      >
                        <SelectTrigger id="advice">
                          <SelectValue placeholder="Selecteer een advies" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Halen">Halen</SelectItem>
                          <SelectItem value="Informatie vragen">Informatie vragen</SelectItem>
                          <SelectItem value="Scouten">Scouten</SelectItem>
                          <SelectItem value="Afwachten niet eerste keus">Afwachten niet eerste keus</SelectItem>
                          <SelectItem value="Niet halen">Niet halen</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Controller
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <Checkbox
                        id="active"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isLoading}
                      />
                    )}
                  />
                  <Label htmlFor="active" className="font-normal cursor-pointer">
                    Actief
                  </Label>
                </div>
              </div>
            </div>
            <DialogFooter className="p-4 border-t">
              <Button 
                onClick={handleConfirm} 
                disabled={!isFormValid || isLoading}
              >
                {isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                Bevestigen
              </Button>
            </DialogFooter>
          </>
      </DialogContent>
    </Dialog>
  );
}